import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../Auth/AuthProvider';
import {
  clearLocalUploadCache,
  openChatSession,
  sendImages as brotiaSendImages,
  sendMessage as brotiaSendMessage,
  signOutOfBrotia,
  subscribeToMessages,
  type BrotiaMessage,
  type SendImagesOptions,
} from '../api/brotia/chat';

const WORKSPACE_ID = 'posy-spa';

const lastReadKey = (conversationId: string) =>
  `brotia:lastReadAt:${conversationId}`;

const isAuthError = (message: string) => {
  const m = message.toLowerCase();
  return (
    m.includes('permission') ||
    m.includes('unauth') ||
    m.includes('token') ||
    m.includes('expired')
  );
};

type BrotiaChatContextValue = {
  isReady: boolean;
  isLoading: boolean;
  error: string | null;
  conversationId: string | null;
  messages: BrotiaMessage[];
  unreadCount: number;
  sendMessage: (text: string) => Promise<void>;
  sendImages: (files: File[], options?: SendImagesOptions) => Promise<boolean>;
  markAsRead: () => void;
  retry: () => void;
  refreshSession: () => Promise<void>;
};

const BrotiaChatContext = createContext<BrotiaChatContextValue | null>(null);

export const useBrotiaChat = (): BrotiaChatContextValue => {
  const ctx = useContext(BrotiaChatContext);
  if (!ctx) {
    throw new Error('useBrotiaChat must be used within BrotiaChatProvider');
  }
  return ctx;
};

export const BrotiaChatProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const { pathname } = useLocation();

  const isAuthenticated = Boolean(user);

  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<BrotiaMessage[]>([]);
  const [lastReadAt, setLastReadAt] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasReceivedSnapshot, setHasReceivedSnapshot] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isMountedRef = useRef(true);
  const sessionTokenRef = useRef(0);
  const buildChatMetadataRef = useRef<
    () => Record<string, string | undefined | null>
  >(() => ({}));

  const start = useCallback(async () => {
    if (!isMountedRef.current) return;
    const myToken = ++sessionTokenRef.current;
    setIsLoading(true);
    setHasReceivedSnapshot(false);
    setError(null);
    try {
      const { conversationId: convId } = await openChatSession(
        WORKSPACE_ID,
        buildChatMetadataRef.current(),
      );
      if (!isMountedRef.current || sessionTokenRef.current !== myToken) return;

      // First-ever session for this conversation: treat history as read.
      const storedRaw = window.localStorage.getItem(lastReadKey(convId));
      const stored = storedRaw ? Number(storedRaw) : NaN;
      const initial = Number.isFinite(stored) ? stored : Date.now();
      if (!Number.isFinite(stored)) {
        window.localStorage.setItem(lastReadKey(convId), String(initial));
      }

      if (!isMountedRef.current || sessionTokenRef.current !== myToken) return;
      setConversationId(convId);
      setLastReadAt(initial);

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      const unsubscribe = await subscribeToMessages(
        convId,
        (next) => {
          if (isMountedRef.current && sessionTokenRef.current === myToken) {
            setMessages(next);
            setHasReceivedSnapshot(true);
          }
        },
        (err) => {
          if (!isMountedRef.current) return;
          if (isAuthError(err.message ?? '')) {
            // Token likely expired (~1h). Re-establish.
            void start();
          } else {
            setError(err.message ?? String(err));
          }
        },
      );
      if (!isMountedRef.current || sessionTokenRef.current !== myToken) {
        unsubscribe();
        return;
      }
      unsubscribeRef.current = unsubscribe;
    } catch (e) {
      if (isMountedRef.current && sessionTokenRef.current === myToken) {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      if (isMountedRef.current && sessionTokenRef.current === myToken) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    if (!isAuthenticated) {
      sessionTokenRef.current++;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      clearLocalUploadCache();
      setConversationId(null);
      setMessages([]);
      setHasReceivedSnapshot(false);
      setError(null);
      setIsLoading(false);
      void signOutOfBrotia().catch(() => undefined);
      return () => {
        isMountedRef.current = false;
      };
    }

    void start();

    return () => {
      isMountedRef.current = false;
      sessionTokenRef.current++;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      void signOutOfBrotia().catch(() => undefined);
    };
  }, [isAuthenticated, start]);

  const buildChatMetadata = useCallback((): Record<
    string,
    string | undefined | null
  > => {
    const store = user?.store;
    const storeName =
      store && typeof store.name === 'string' ? store.name : undefined;

    return {
      screen: pathname || undefined,
      platform: 'web',
      locale: i18n.language,
      env: process.env.REACT_APP_ENV,
      storeId: user?.storeId,
      storeName,
      userId: user?.id,
      userRole: user?.isAdmin ? 'owner' : 'collaborator',
      subscription: user?.plan,
    };
  }, [user, i18n.language, pathname]);

  useEffect(() => {
    buildChatMetadataRef.current = buildChatMetadata;
  }, [buildChatMetadata]);

  const refreshSession = useCallback(async () => {
    if (!conversationId) return;
    try {
      await openChatSession(WORKSPACE_ID, buildChatMetadata());
    } catch (e) {
      // Best-effort: a metadata refresh shouldn't surface as a chat error,
      // and the listener stays attached to the existing conversation.
      console.warn('Brotia metadata refresh failed:', e);
    }
  }, [conversationId, buildChatMetadata]);

  const sendMessage = useCallback(async (text: string) => {
    await brotiaSendMessage(text);
  }, []);

  const sendImages = useCallback(
    async (files: File[], options?: SendImagesOptions) => {
      if (!conversationId) {
        throw new Error('No active chat session');
      }
      return brotiaSendImages(conversationId, files, options);
    },
    [conversationId],
  );

  const markAsRead = useCallback(() => {
    if (!conversationId) return;
    const latest = messages.reduce(
      (max, m) => Math.max(max, m.createdAt.getTime()),
      Date.now(),
    );
    setLastReadAt(latest);
    window.localStorage.setItem(lastReadKey(conversationId), String(latest));
  }, [conversationId, messages]);

  const retry = useCallback(() => {
    void start();
  }, [start]);

  const unreadCount = useMemo(
    () =>
      messages.filter(
        (m) => m.direction === 'outbound' && m.createdAt.getTime() > lastReadAt,
      ).length,
    [messages, lastReadAt],
  );

  const value = useMemo<BrotiaChatContextValue>(
    () => ({
      isReady: conversationId !== null && !isLoading && hasReceivedSnapshot,
      isLoading,
      error,
      conversationId,
      messages,
      unreadCount,
      sendMessage,
      sendImages,
      markAsRead,
      retry,
      refreshSession,
    }),
    [
      conversationId,
      isLoading,
      hasReceivedSnapshot,
      error,
      messages,
      unreadCount,
      sendMessage,
      sendImages,
      markAsRead,
      retry,
      refreshSession,
    ],
  );

  return (
    <BrotiaChatContext.Provider value={value}>
      {children}
    </BrotiaChatContext.Provider>
  );
};

export default BrotiaChatProvider;
